/**
 * Permessi contestuali sulla singola pratica, non derivabili dal solo ruolo.
 *
 * Il controllore dei documenti (chi puo' approvarli o rifiutarli) dipende da
 * chi e' il fornitore e da eventuali takeover espliciti:
 *  - Fornitore HQ-internal       -> controllore = Fastweb (HQ non puo' controllare se stessa)
 *  - Fornitore normale + flag
 *    `inCaricoFastweb` attivato  -> controllore = Fastweb (Fastweb ha preso in carico)
 *  - Fornitore normale (default) -> controllore = HQ
 *
 * Entrambi i flag, una volta impostati a `true`, sono FROZEN per il resto della
 * vita della pratica (vedi rules: niente transizione true -> false). Il flag
 * `fornitoreIsInternalHq` ha precedenza su `inCaricoFastweb` (per coerenza con
 * il significato semantico: HQ-internal e' una condizione strutturale, takeover
 * e' un'azione operativa).
 */

import { ROLES } from '@/lib/roles';
import type { Role } from '@/lib/roles';
import { isEtoProfile } from '@/lib/territorio';
import type { TerritorialProfile } from '@/lib/territorio';

export interface PracticeWithController {
  fornitoreIsInternalHq?: boolean | null;
  inCaricoFastweb?: boolean | null;
  workflowStatus?: string | null;
}

export type DocumentControllerRole = typeof ROLES.HQ | typeof ROLES.FASTWEB;

/** Ritorna il ruolo che deve approvare i documenti di questa pratica. */
export function getDocumentController(
  practice: PracticeWithController | null | undefined
): DocumentControllerRole {
  if (practice?.fornitoreIsInternalHq === true) return ROLES.FASTWEB;
  if (practice?.inCaricoFastweb === true) return ROLES.FASTWEB;
  return ROLES.HQ;
}

/**
 * L'utente puo' approvare i documenti di questa pratica.
 * Per il ruolo Fastweb serve anche il profilo ETO (`isEto === true`): gli
 * utenti Fastweb territoriali sono in sola consultazione. Profilo assente =
 * non ETO (default sicuro). HQ non richiede profilo.
 */
export function canApproveDocuments(
  userRole: string | null | undefined,
  practice: PracticeWithController | null | undefined,
  profile?: TerritorialProfile | null
): boolean {
  if (userRole !== getDocumentController(practice)) return false;
  if (userRole === ROLES.FASTWEB) return isEtoProfile(profile);
  return true;
}

/** L'utente puo' rifiutare i documenti di questa pratica (stesso gate ETO per Fastweb). */
export function canRejectDocuments(
  userRole: string | null | undefined,
  practice: PracticeWithController | null | undefined,
  profile?: TerritorialProfile | null
): boolean {
  if (userRole !== getDocumentController(practice)) return false;
  if (userRole === ROLES.FASTWEB) return isEtoProfile(profile);
  return true;
}

/**
 * Stati workflow in cui Fastweb puo' "prendere in carico" una pratica di un
 * fornitore non-internal-HQ. Negli stati successivi il flusso documenti e' gia'
 * concluso (approvazione_hq, chiuso, rifiutato_ditta) o il flag sarebbe inutile
 * (rifiutato: il fornitore deve riaprire prima di qualsiasi altra azione).
 */
const TAKEOVER_ALLOWED_STATES = ['non_avviato', 'in_corso'];

/**
 * L'utente puo' attivare il "Fastweb takeover" su questa pratica.
 *  - solo Fastweb con profilo ETO (gli utenti territoriali sono in sola
 *    consultazione — profilo assente = non ETO, default sicuro),
 *  - solo su pratiche con fornitore non-internal-HQ (altrimenti Fastweb e'
 *    gia' controllore automatico, la casella sarebbe ridondante),
 *  - solo se non gia' presa in carico (frozen una volta attivato),
 *  - solo negli stati TAKEOVER_ALLOWED_STATES.
 */
export function canFastwebTakeover(
  userRole: string | null | undefined,
  practice: PracticeWithController | null | undefined,
  profile?: TerritorialProfile | null
): boolean {
  if (userRole !== ROLES.FASTWEB) return false;
  if (!isEtoProfile(profile)) return false;
  if (!practice) return false;
  if (practice.fornitoreIsInternalHq === true) return false;
  if (practice.inCaricoFastweb === true) return false;
  if (!practice.workflowStatus) return false;
  return TAKEOVER_ALLOWED_STATES.includes(practice.workflowStatus);
}

/**
 * Pratica esplicitamente presa in carico da un utente Fastweb.
 * Usato per mostrare il badge giallo "In carico a Fastweb" nella UI.
 * NB: distinto da `getDocumentController(p) === 'fastweb'`, che e' true anche
 * per pratiche HQ-internal (quelle hanno una loro indicazione separata).
 */
export function hasFastwebTakeover(
  practice: PracticeWithController | null | undefined
): boolean {
  return practice?.inCaricoFastweb === true;
}
